// src/components/BoardPack/pages/donut_chart_18.jsx

import React, { useState, useEffect, useRef } from 'react';
import { apiCall } from '@/api/apiCall';
import DonutChart3 from '../components/charts/DonutChart3';
import ErrorState from '@/components/common/ErrorStateComponent';
import LoadingComponent from '@/components/common/LoadingComponent';
import { useQueue } from '@/context/QueueContext';
import HeadingComponent from '../components/shared/heading';
import { processParams } from '../utils/paramsHelper';
import { useNavigate } from 'react-router-dom';

const COLORS = {
    CASH: '#FF8A3C',
    CARD: '#48A5F6',
    POS: '#48A5F6',
    APM: '#2E7ABF',
    APP_WEB: '#c9611a',
    VERIFIER: '#FF9044',
    // value_type 4 colors
    PND: '#FF8A3C',
    COINS: '#48A5F6',
    EWALLET: '#2E7ABF',
    NOTES: '#FF9044',
    VERIFIER_SALIK:"#FF8A3C",
    VALET:"#FF8A3C"
};

const DonutChart18 = ({ year = "", month = "", monthNumber = "", contract = "", page = null,isRefresh = false }) => {
    const { enqueue } = useQueue();
    const navigate = useNavigate();
    const showSalik = page?.showSalik === "true" ||page?.showSalik === true  ? true : false;
    const showValet = page?.showValet === "true" ||page?.showValet === true  ? true : false;


    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cashCardData, setCashCardData] = useState([]);
    const [paymentTypeData, setPaymentTypeData] = useState([]);
    const hasFetchedRef = useRef(false);

    useEffect(() => {
        if (!year || !month || !contract) return;
        if (hasFetchedRef.current) return;

        hasFetchedRef.current = true;
        fetchParkingKPI();
    }, [year, month, contract]);

    const fetchParkingKPI = async () => {
        try {
            setLoading(true);
            setError(null);

            const apiParams = processParams(page?.params || {}, { year, month, contract });

            const response = await enqueue(() => apiCall(
                'GET',
                page?.api || '/boardpack/payment-sources',
                apiParams,
                navigate,
                false,
                false,
                isRefresh
            ));

            if (!response?.success) {
                throw new Error(response?.message || 'Failed to fetch parking data');
            }

            const contractId = apiParams.contract || page?.params?.contract;
            const contractData = response.data?.[contractId] || {};
            const years = Object.keys(contractData).sort();
            const valueType = Number(apiParams.value_type || page?.params?.value_type || 1);

            let newCashCard = [];
            let newPaymentTypes = [];

            if (valueType === 1) {
                // Overall Payment Split: Cash vs Card | Payment Location
                newCashCard = years.map((yr) => {
                    const yData = contractData[yr];
                    return {
                        year: yr,
                        cash: Number(yData?.cashCard?.cash?.percent_value?.toFixed(2) || 0),
                        card: Number(yData?.cashCard?.card?.percent_value?.toFixed(2) || 0),
                    };
                });

                newPaymentTypes = years.map((yr) => {
                    const yData = contractData[yr];
                    return {
                        year: yr,
                        pos: Number(yData?.payLocation?.POS?.percent_value?.toFixed(2) || 0),
                        apm: Number(yData?.payLocation?.APM?.percent_value?.toFixed(2) || 0),
                        appWeb: Number(yData?.payLocation?.['APP/WEB']?.percent_value?.toFixed(2) || 0),
                        verifier: Number(yData?.payLocation?.VERIFIER?.percent_value?.toFixed(2) || 0),
                        verifierSalik: Number(yData?.payLocation?.VERIFIER_SALIK?.percent_value?.toFixed(2) || 0),
                        VALET: Number(yData?.payLocation?.VALET?.percent_value?.toFixed(2) || 0),
                    };
                });
            }
            else if (valueType === 2) {
                // Cash Source Split (POS vs APM) | Card Source Split
                newCashCard = years.map((yr) => {
                    const yData = contractData[yr];
                    return {
                        year: yr,
                        cash: Number(yData?.cashSourceSplit?.POS?.percent_value?.toFixed(2) || 0),
                        card: Number(yData?.cashSourceSplit?.APM?.percent_value?.toFixed(2) || 0),
                    };
                });

                newPaymentTypes = years.map((yr) => {
                    const yData = contractData[yr];
                    return {
                        year: yr,
                        pos: Number(yData?.cardSourceSplit?.POS?.percent_value?.toFixed(2) || 0),
                        apm: Number(yData?.cardSourceSplit?.APM?.percent_value?.toFixed(2) || 0),
                        appWeb: Number(yData?.cardSourceSplit?.['APP/WEB']?.percent_value?.toFixed(2) || 0),
                        verifier: Number(yData?.cardSourceSplit?.VERIFIER?.percent_value?.toFixed(2) || 0),
                        verifierSalik: Number(yData?.cardSourceSplit?.VERIFIER_SALIK?.percent_value?.toFixed(2) || 0),
                        VALET: Number(yData?.cardSourceSplit?.VALET?.percent_value?.toFixed(2) || 0),
                    };
                });
            }
            else if (valueType === 3) {
                // Source Cash-Card Split: APM (Cash vs Card) | POS (Cash vs Card)
                newCashCard = years.map((yr) => {
                    const yData = contractData[yr]?.sourceCashCardSplit || {};
                    const apm = yData.APM || {};
                    return {
                        year: yr,
                        cash: Number(apm.cash?.percent_value?.toFixed(2) || 0),
                        card: Number(apm.card?.percent_value?.toFixed(2) || 0),
                    };
                });

                newPaymentTypes = years.map((yr) => {
                    const yData = contractData[yr]?.sourceCashCardSplit || {};
                    const pos = yData.POS || {};
                    return {
                        year: yr,
                        pos: Number(pos.cash?.percent_value?.toFixed(2) || 0),
                        apm: Number(pos.card?.percent_value?.toFixed(2) || 0),
                        appWeb: 0,
                        verifier: 0,
                        verifierSalik: Number(yData?.VERIFIER_SALIK?.percent_value?.toFixed(2) || 0),
                        VALET: Number(yData?.VALET?.percent_value?.toFixed(2) || 0),
                    };
                });
            }
            else if (valueType === 4) {
                // PND vs APP/WEB split | PND breakdown (COINS, EWALLET, NOTES)

                // First chart: PND vs APP/WEB
                newCashCard = years.map((yr) => {
                    const yData = contractData[yr];
                    return {
                        year: yr,
                        pnd: Number(yData?.pndAppWeb?.PND?.percent_value?.toFixed(2) || 0),
                        appWeb: Number(yData?.pndAppWeb?.['APP/WEB']?.percent_value?.toFixed(2) || 0),
                    };
                });

                // Second chart: PND split (COINS, EWALLET, NOTES)
                newPaymentTypes = years.map((yr) => {
                    const yData = contractData[yr];
                    return {
                        year: yr,
                        coins: Number(yData?.pndSplit?.['PND :: COINS']?.percent_value?.toFixed(2) || 0),
                        ewallet: Number(yData?.pndSplit?.['PND :: EWALLET']?.percent_value?.toFixed(2) || 0),
                        notes: Number(yData?.pndSplit?.['PND :: NOTES']?.percent_value?.toFixed(2) || 0),
                        verifierSalik: Number(yData?.payLocation?.VERIFIER_SALIK?.percent_value?.toFixed(2) || 0),
                        VALET: Number(yData?.payLocation?.VALET?.percent_value?.toFixed(2) || 0),
                    };
                });
            }

            setCashCardData(newCashCard);
            setPaymentTypeData(newPaymentTypes);

        } catch (err) {
            console.error('Failed to load KPI data:', err);
            setError(err.message || 'Failed to load parking data');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <LoadingComponent />;
    if (error) return <ErrorState error={error} onRetry={fetchParkingKPI} />;

    const title = page?.title;
    const subtitle = page?.subtitle;
    const in_chart_content_line_1 = page?.in_chart_content_line_1;
    const in_chart_content_line_2 = page?.in_chart_content_line_2;

    const valueType = Number(page?.params?.value_type || 1);

    // ── First Chart Config ───────────────────────────────────────────────────
    let firstChartLabels = ['Cash', 'Card'];
    let firstChartColors = [COLORS.CASH, COLORS.CARD];
    let firstChartLegend = [
        { label: 'CASH', color: COLORS.CASH },
        { label: 'CARD', color: COLORS.CARD },
    ];

    if (valueType === 2) {
        firstChartLabels = ['POS', 'APM'];
        firstChartColors = [COLORS.CASH, COLORS.CARD];
        firstChartLegend = [
            { label: 'POS', color: COLORS.CASH },
            { label: 'APM', color: COLORS.CARD },
        ];
    } else if (valueType === 4) {
        firstChartLabels = ['PND', 'APP/WEB'];
        firstChartColors = [COLORS.PND, COLORS.APP_WEB];
        firstChartLegend = [
            { label: 'PND', color: COLORS.PND },
            { label: 'APP/WEB', color: COLORS.APP_WEB },
        ];
    }

    // ── Second Chart Config ──────────────────────────────────────────────────
    let secondChartLabels = ['POS', 'APM', 'APP/WEB', 'VERIFIER'];
    let secondChartColors = [COLORS.POS, COLORS.APM, COLORS.APP_WEB, COLORS.VERIFIER];
    let secondChartLegend = [
        { label: 'POS', color: COLORS.POS },
        { label: 'APM', color: COLORS.APM },
        { label: 'APP/WEB', color: COLORS.APP_WEB },
        { label: 'VERIFIER', color: COLORS.VERIFIER },
    ];

    if (valueType === 3) {
        secondChartLabels = ['Cash', 'Card'];
        secondChartColors = [COLORS.POS, COLORS.APM];
        secondChartLegend = [
            { label: 'CASH', color: COLORS.POS },
            { label: 'CARD', color: COLORS.APM },
        ];
    } else if (valueType === 4) {
        secondChartLabels = ['COINS', 'EWALLET', 'NOTES'];
        secondChartColors = [COLORS.COINS, COLORS.EWALLET, COLORS.NOTES];
        secondChartLegend = [
            { label: 'COINS', color: COLORS.COINS },
            { label: 'EWALLET', color: COLORS.EWALLET },
            { label: 'NOTES', color: COLORS.NOTES },
        ];
    }
    if (showSalik) {
    secondChartLabels.push('VERIFIER SALIK');
    secondChartColors.push(COLORS.VERIFIER_SALIK);
    secondChartLegend.push( { label: 'VERIFIER SALIK', color: COLORS.VERIFIER_SALIK });
}
    if (showValet) {
    secondChartLabels.push('VALET');
    secondChartColors.push(COLORS.VALET);
    secondChartLegend.push( { label: 'VALET', color: COLORS.VALET });
}

    // ── Helper: get chart data array per value_type ──────────────────────────
    const getFirstChartData = (d) => {
        if (valueType === 4) return [d.pnd, d.appWeb];
        return [d.cash, d.card]; // type 1, 2, 3
    };

    // const getSecondChartData = (d) => {
    //     if (valueType === 3) return [d.pos, d.apm];
    //     if (valueType === 4) return [d.coins, d.ewallet, d.notes];
    //     return [d.pos, d.apm, d.appWeb, d.verifier]; // type 1 & 2
    // };

    const getSecondChartData = (d) => {
    let data = [];

    if (valueType === 3) {
        data = [d.pos, d.apm];
    } 
    else if (valueType === 4) {
        data = [d.coins, d.ewallet, d.notes];
    } 
    else {
        data = [d.pos, d.apm, d.appWeb, d.verifier];
    }

    if (showSalik ) {
        data.push(d.verifierSalik);
    }
    if (showValet ) {
        data.push(d.VALET);
    }

    return data;
};
    return (
        <div className="w-full px-5 pt-5 pb-18 bg-background">
            <div className="w-full mx-auto">
                {/* Heading */}
                {/* <div className="text-center">
                    {title && (
                        <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                            {title}
                        </h1>
                    )}
                    {subtitle && (
                        <h2 className="text-xl md:text-2xl font-semibold text-muted-foreground">
                            {subtitle}
                        </h2>
                    )}
                </div> */}
                <div className="flex flex-col gap-0.5 mb-1">
        {
          title && <HeadingComponent variant={"main"}>
            {title}
          </HeadingComponent>
        }
        {
          subtitle && <HeadingComponent variant={"sub"}>
            {subtitle}
          </HeadingComponent>
        }
      </div>

                <div className="bg-red-700 dark:bg-red-600 h-6 w-full" />
                <div className="bg-gray-900 dark:bg-gray-950 py-4">

                    {/* First Chart Section */}
                    <div className="overflow-hidden shadow-lg">
                        {/* {in_chart_content_line_1 && (
                            <h3 className="text-center font-bold text-xs mb-2 text-white">
                                {in_chart_content_line_1}
                            </h3>
                        )} */}
                        {in_chart_content_line_1 && (
    <h3 className="text-center font-bold text-xs mb-2 text-white">
        {in_chart_content_line_1.split(/(\([^)]+\))/g).map((part, index) => {
            if (part.startsWith('(') && part.endsWith(')')) {
                return (
                    <span key={index} className="text-yellow-400">
                        {part}
                    </span>
                );
            }
            return part;
        })}
    </h3>
)}
                        <div className="grid grid-cols-3 gap-12 place-items-center mx-4 py-12 bg-white dark:bg-gray-800">
                            {cashCardData.map((data) => (
                                <div key={data.year} className="flex justify-center items-center">
                                    <DonutChart3
                                        data={getFirstChartData(data)}
                                        labels={firstChartLabels}
                                        colors={firstChartColors}
                                        year={data.year}
                                        legendItems={firstChartLegend}
                                        size={280}
                                        cutoutPercentage={65}
                                        labelPosition="outside"
                                        labelStyle="simple"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Second Chart Section */}
                    <div className="overflow-hidden shadow-lg">
                        {/* {in_chart_content_line_2 && (
                            <h3 className="text-center font-bold my-2 text-xs text-white">
                                {in_chart_content_line_2}
                            </h3>
                        )} */}

                        {in_chart_content_line_2 && (
    <h3 className="text-center font-bold my-2 text-xs text-white">
        {in_chart_content_line_2.split(/(\([^)]+\))/g).map((part, index) => {
            if (part.startsWith('(') && part.endsWith(')')) {
                return (
                    <span key={index} className="text-yellow-400">
                        {part}
                    </span>
                );
            }
            return part;
        })}
    </h3>
)}
                        <div className="grid grid-cols-3 gap-12 place-items-center mx-4 py-12 bg-white dark:bg-gray-800">
                            {paymentTypeData.map((data) => (
                                <div key={data.year} className="flex justify-center items-center">
                                    <DonutChart3
                                        data={getSecondChartData(data)}
                                        labels={secondChartLabels}
                                        colors={secondChartColors}
                                        year={data.year}
                                        legendItems={secondChartLegend}
                                        size={280}
                                        cutoutPercentage={65}
                                        labelPosition="outside"
                                        labelStyle="detailed"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default DonutChart18;